import { useState } from "react";
import { styled } from "@mui/material/styles";
import {
    Box,
    Button,
    Card,
    CardContent,
    GridProps,
    InternalStandardProps,
    Typography,
} from "@mui/material";

import FallbackWaveform from "Images/Content/FallbackWaveform.png";

export interface MixedContentItemProps
    extends InternalStandardProps<GridProps> {
    title: string;
    description: string;
    contentType: string;
    imageUrl: string;
    onListItemClick: React.MouseEventHandler<HTMLDivElement>;
    itemReference: (ref: any) => void;
}

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: `${theme.palette.background.default} !important`,
    display: "flex",
    alignItems: "center",
    cursor: "pointer !important",
    marginBottom: "20px",
}));

const StyledCardContent = styled(CardContent)({
    width: "100%",
    padding: "8px !important",
    overflow: "hidden",
});

const StyledImageContainer = styled(Box)({
    width: "80px",
    display: "flex",
    justifyContent: "center",
    position: "relative",
});

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontSize: "18px",
    color: theme.palette.grey[600],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

interface StyledDescriptionProps {
    expanded: boolean;
}

const StyledDescription = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "expanded",
})<StyledDescriptionProps>(({ theme, expanded }) => ({
    color: theme.palette.grey[600],
    fontSize: "12px",
    ...(expanded
        ? {
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
          }
        : {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
          }),
}));

const StyledMoreButton = styled(Button)(({ theme }) => ({
    color: theme.palette.primary.main,
    padding: "0",
    paddingLeft: "10px",
}));

const StyledMoreText = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    textTransform: "lowercase",
    fontSize: "12px",
    fontWeight: 500,
}));

const StyledMediaType = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[600],
    fontSize: "17px",
}));

export const MixedContentItem = (props: MixedContentItemProps) => {
    const {
        title,
        description,
        contentType,
        imageUrl,
        onListItemClick,
        itemReference,
    } = props;
    const [moreDescription, setMoreDescription] = useState<boolean>(false);

    return (
        <StyledCard
            ref={(el) => itemReference(el)}
            elevation={0}
        >
            <StyledImageContainer onClick={onListItemClick}>
                <img
                    src={imageUrl || FallbackWaveform}
                    style={{ width: "60px" }}
                    alt="thumbnail"
                />
            </StyledImageContainer>
            <StyledCardContent onClick={onListItemClick}>
                <StyledTitle>
                    {title}
                </StyledTitle>
                <StyledDescription expanded={moreDescription}>
                    {description}
                </StyledDescription>
                <Box style={{ display: "flex", justifyContent: "flex-end" }}>
                    <StyledMoreButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setMoreDescription(!moreDescription);
                        }}
                    >
                        <StyledMoreText>
                            {moreDescription ? "Less" : "More"}
                        </StyledMoreText>
                    </StyledMoreButton>
                </Box>
                <StyledMediaType>
                    {`Content Type: ${contentType}`}
                </StyledMediaType>
            </StyledCardContent>
        </StyledCard>
    );
};
